import { flow, types } from "mobx-state-tree";
import { masterAdminApi } from "../../api";
import { notification } from "../../utils/notifications";
import { constRoute } from "@utils/route";
import { catchError, onMasterLogOutClearAll } from "@utils/common-functions";
import { allUsersType } from "@stores/store-utils";
import { toJS } from "mobx";

export const user = types
  .model({
    currentUserData: types.maybeNull(types.frozen()),
    loadingLogin: types.optional(types.boolean, false),
    loadingMasterAdmin: types.optional(types.boolean, false),
    allUsers: types.optional(allUsersType, {}),
  })
  .views((self) => ({
    get getAllUsers() {
      return toJS(self.allUsers);
    },
    get isLoadingLogin() {
      return self.loadingLogin;
    },
    get isLoadingMasterAdmin() {
      return toJS(self.loadingMasterAdmin);
    },
  }))
  .actions((self) => {
    const onSuperAdminLogin = flow(function* (data, navigate, resetPassword) {
      self.loadingLogin = true;
      try {
        const res = yield masterAdminApi.login(data);

        if (!res?.jwt_token) throw new Error("Missing token");

        localStorage.setItem("master_token", res.jwt_token);
        localStorage.setItem("master_email", data.email);
        localStorage.setItem(
          "master_userData",
          JSON.stringify({ name: res.name, email: res.email }),
        );

        self.currentUserData = res;

        navigate(constRoute.masteradminDashboard);
        notification.success("Signed in successfully");
      } catch (error) {
        resetPassword();
        catchError(error, "onSuperAdminLogin");
      } finally {
        self.loadingLogin = false;
      }
    });

    const receiveAllUsers = flow(function* () {
      let response;
      try {
        const res = yield masterAdminApi.receiveAllUsers();
        if (res) {
          const rawAccountHolders =
            res?.["account holders"] ??
            res?.account_holders ??
            res?.accountHolders ??
            null;
          const accountHoldersArray = Array.isArray(rawAccountHolders)
            ? rawAccountHolders
            : rawAccountHolders
              ? Object.values(rawAccountHolders)
              : [];

          const normalizedUsers = accountHoldersArray.map((user) => ({
            ...user,
            entity_type: user.entity_type ?? user.entity ?? null,
          }));

          self.allUsers = {
            ...res,
            "account holders": normalizedUsers,
            "total users":
              res?.["total users"] ??
              res?.total_users ??
              res?.totalUsers ??
              normalizedUsers.length,
          };
        }
        response = res;
      } catch (error) {
        catchError(error, "receiveAllUsers");
      } finally {
        return response;
      }
    });

    const setAllUsers = (users) => {
      self.allUsers = users;
    };

    const onDeleteUser = flow(function* (email) {
      let response;

      try {
        response = yield masterAdminApi.deleteUserByEmail(email);
      } catch (error) {
        catchError(error, "onDeleteUser");
      } finally {
        return response;
      }
    });

    const onUpdateUserPlan = flow(function* ({ email, new_plan }) {
      let response;

      try {
        response = yield masterAdminApi.updateUserPlan({ email, new_plan });
      } catch (error) {
        catchError(error, "onUpdateUserPlan");
      } finally {
        return response;
      }
    });

    const onDowngradeUserPlan = flow(function* ({ email, new_plan }) {
      let response;

      try {
        response = yield masterAdminApi.downgradeUserPlan({ email, new_plan });
      } catch (error) {
        catchError(error, "onDowngradeUserPlan");
      } finally {
        return response;
      }
    });

    const addSubAccounts = flow(function* ({ email, subaccount_email }) {
      let response;

      try {
        response = yield masterAdminApi.addUsersToAccount({
          email,
          subaccount_email,
        });
      } catch (error) {
        catchError(error, "addSubAccounts");
      } finally {
        return response;
      }
    });

    const addPurchasedTokens = flow(function* ({ email, new_credits, tool }) {
      let response;

      try {
        response = yield masterAdminApi.purchaseTokens({
          email,
          new_credits,
          tool,
        });
      } catch (error) {
        catchError(error, "addPurchasedTokens");
      } finally {
        return response;
      }
    });

    const handleGetFiles = flow(function* (data) {
      let response;

      try {
        response = yield masterAdminApi.getFiles(data);
      } catch (error) {
        catchError(error, "handleGetFiles");
      } finally {
        return response;
      }
    });

    const handleGetFileInfo = flow(function* (data) {
      let response;

      try {
        response = yield masterAdminApi.getFileInfo(data);
      } catch (error) {
        catchError(error, "handleGetFileInfo");
      } finally {
        return response;
      }
    });

    const handleDeleteFile = flow(function* (data) {
      let response;

      try {
        response = yield masterAdminApi.removeFile(data);
      } catch (error) {
        catchError(error, "handleDeleteFile");
      } finally {
        return response;
      }
    });

    const handleCreateFile = flow(function* (data) {
      let response;

      try {
        response = yield masterAdminApi.createFile(data);
      } catch (error) {
        catchError(error, "handleCreateFile");
      } finally {
        return response;
      }
    });

    const getUserDetails = flow(function* (data) {
      self.loadingMasterAdmin = true;
      let response = null;

      try {
        const res = yield masterAdminApi.getUserData(data);
        response = res;
      } catch (error) {
        catchError(error, "getUserDetails");
      } finally {
        self.loadingMasterAdmin = false;
        return response;
      }
    });

    const loadMasterAdminInfo = flow(function* () {
      self.loadingMasterAdmin = true;
      let response = null;
      try {
        const res = yield masterAdminApi.getCurrentUserDetails();
        response = res;
        self.currentUserData = res;
      } catch (error) {
        catchError(error, "loadMasterAdminInfo");
        response = error?.response;
        if (
          error?.response?.data?.error?.includes("Invalid token") ||
          error?.response?.data?.error?.includes("Token has expired")
        ) {
          onMasterLogOutClearAll(constRoute.masteradminLogin);
        }
      } finally {
        self.loadingMasterAdmin = false;
        return response;
      }
    });

    return {
      onSuperAdminLogin,
      receiveAllUsers,
      setAllUsers,
      onDeleteUser,
      onUpdateUserPlan,
      onDowngradeUserPlan,
      addSubAccounts,
      addPurchasedTokens,
      getUserDetails,
      loadMasterAdminInfo,
      handleGetFiles,
      handleGetFileInfo,
      handleDeleteFile,
      handleCreateFile,
    };
  });

export function initUser() {
  return user.create({});
}
